import React, { useContext } from "react";

import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "@mui/material";
import { Clipboard } from "react-feather";
import { Link as RouterLink, useHistory } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import KebabMenu from "components/shared/kebab-menu";
import { scvLink, subscriptionTypeNameDisplayMapping } from "constants/index";
import { subscriptionStateDisplayMapping } from "constants/subscription";
import { SnackbarContext } from "context/snackbar-context";
import { findSubscriptionIndex } from "helpers/find-subscription-index";
import useNavRedesign from "helpers/hooks/use-nav-redesign";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { SearchTableBodyRowOrgSearchProps } from "types/search-display";

const SearchTableBodyRowOrgSearch = ({
  row,
  regionCode,
}: SearchTableBodyRowOrgSearchProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const history = useHistory();
  const navRedesign = useNavRedesign();
  const manageOrgHref = `/organisation/${regionCode}/${row.id}`;
  const tableauHref = `${scvLink}${row.id}`;
  const { copyToClipboard } = useContext(SnackbarContext);

  const subscriptionTypeChip = () => {
    if (row.subscription.length) {
      const type = row.subscription[findSubscriptionIndex(row)].type;
      const chipClass =
        type == SubscriptionType.FREE_ORG
          ? classes.yellowSubmarine
          : classes.purpleRain;
      return (
        <Chip
          size="small"
          color="primary"
          sx={{ ...chipClass }}
          label={subscriptionTypeNameDisplayMapping[type]}
          clickable
          onClick={() => history.push(manageOrgHref)}
        />
      );
    }
  };

  const subscriptionStateChipStyleMap = {
    [SubscriptionState.UNSPECIFIED]: classes.neutral,
    [SubscriptionState.ACTIVE]: classes.greenDay,
    [SubscriptionState.TRIALING]: classes.blueMoon,
    [SubscriptionState.PAST_DUE]: classes.yellowSubmarine,
    [SubscriptionState.CANCELED]: classes.redWine,
  };

  // Provider-agnostic state chip (Chargify or Zuora), sourced from
  // subscriptionData. Suppressed for Free plan orgs, since stale/leftover
  // state (e.g. CANCELED) can persist from a prior paid subscription.
  const subscriptionStateChip = () => {
    const isFreeOrg =
      row.subscription.length > 0 &&
      row.subscription[findSubscriptionIndex(row)].type ===
        SubscriptionType.FREE_ORG;
    const state = row.subscriptionData?.state;
    if (
      !isFreeOrg &&
      state !== undefined &&
      state !== SubscriptionState.UNSPECIFIED
    ) {
      return (
        <Chip
          size="small"
          color="primary"
          data-testid="subscription-chip"
          sx={{
            ...subscriptionStateChipStyleMap[state],
            ...classes.basicSpacing,
          }}
          label={subscriptionStateDisplayMapping[state]}
          clickable
          onClick={() => history.push(manageOrgHref)}
        />
      );
    }
  };

  const displayNumMembers = () => {
    if (row.members.length === 1) {
      return `${row.members.length} Member`;
    }

    return `${row.members.length} Members`;
  };

  if (navRedesign) {
    return (
      <TableRow>
        <TableCell sx={{ border: 0, py: 0.75, px: 0 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Link
                    component={RouterLink}
                    to={manageOrgHref}
                    sx={{ ...classes.removeLinkStyle, fontWeight: 700 }}
                  >
                    {row.name}
                  </Link>
                  {subscriptionTypeChip()}
                  {subscriptionStateChip()}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  {displayNumMembers()}
                  {" · Registered "}
                  {row.createdAt &&
                    formatDateTimeDisplay(
                      convertTimestampToDate(row.createdAt),
                    )}
                  {" · "}
                  <Box
                    component="span"
                    onClick={() => copyToClipboard(row.id)}
                    sx={{ ...classes.clickable, cursor: "pointer" }}
                  >
                    {row.id}{" "}
                    <Clipboard size={12} style={{ verticalAlign: "middle" }} />
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Link
                  component={RouterLink}
                  to={manageOrgHref}
                  sx={classes.removeLinkStyle}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    data-anchor="org-manage-btn"
                  >
                    Manage
                  </Button>
                </Link>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  href={tableauHref}
                  target="_blank"
                  sx={{ whiteSpace: "nowrap" }}
                  data-testid="org-tableau-scv-btn"
                >
                  Tableau SCV
                </Button>
                <KebabMenu>
                  <MenuItem onClick={() => copyToClipboard(row.id)}>
                    Copy ID
                  </MenuItem>
                </KebabMenu>
              </Box>
            </Box>
          </Paper>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={9}>
            <h3>
              <Link
                component={RouterLink}
                to={manageOrgHref}
                sx={classes.removeLinkStyle}
              >
                {row.name}
              </Link>{" "}
              {subscriptionTypeChip()}
              {subscriptionStateChip()}
            </h3>
            <div onClick={() => copyToClipboard(row.id)}>
              <Typography sx={classes.clickable} variant="body1" gutterBottom>
                {row.id}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{displayNumMembers()}</Typography>
            </div>

            <Typography variant="body1" paddingTop="5px">
              <b>Date registered: </b>
              {row.createdAt &&
                formatDateTimeDisplay(
                  convertTimestampToDate(row.createdAt),
                )}{" "}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sm={3}
            container
            spacing={2}
            alignItems="flex-end"
            direction="column"
            style={{ marginBottom: 10, marginTop: 15 }}
          >
            <Grid item>
              <Link
                component={RouterLink}
                to={manageOrgHref}
                sx={classes.removeLinkStyle}
              >
                <Button
                  variant="contained"
                  color="primary"
                  data-anchor="org-manage-btn"
                >
                  Manage Organization
                </Button>
              </Link>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                href={tableauHref}
                target="_blank"
                color="primary"
                data-testid="org-tableau-scv-btn"
              >
                Tableau SCV
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default SearchTableBodyRowOrgSearch;
